// gamParams.js — GAM/GPT ad-request "decoder ring".
//
// explainGamUrl(url) parses a GAM ad-request URL (or any URL — it degrades
// gracefully for non-GAM hosts) and returns a human-readable breakdown of
// every query param plus a decode of the cust_params form-encoded blob and
// a set of "missing but expected" flags (privacy signals, correlator, etc).
//
// Never throws.
//
// RELATIONSHIP TO proxy-server/conformance/gamParams.js (reconciled 2026-08-13):
// That file is a narrow, dependency-free url-vs-custParams LOCATION lookup
// (~39 params + hb_*/amzn* pattern fallback) feeding the NL→Check compiler's
// source-correction algorithm — it has no use for prose or display metadata.
// This file is the much wider human-facing decoder ring (name + prose
// meaning + display category, ~54 params total) powering the GAM Explainer
// Tools page as a synchronous, pure, network-free call per pasted URL — it
// has no use for a check-compiler correction algorithm.
//
// Audited 2026-08-13: everywhere both files know the same param name they
// agree on its location (checked exhaustively — zero contradictions found).
// A shared table was considered and rejected as not worth it (this file's
// payload is dominated by text the other file has no use for). Instead,
// proxy-server/conformance/gamParams.sync.test.js (node --test, run from
// proxy-server/) reads THIS file as text and asserts neither side ever
// contradicts the other's location claim for a shared param name. Coverage
// drift (a param known to only one side) is expected and NOT flagged — only
// a location DISAGREEMENT fails that guard. Keep param objects here as
// literal top-level 'key: { ... }' entries inside GAM_PARAM_DICTIONARY /
// CUST_PARAM_MEANINGS (not spread/computed) so the text-based guard can keep
// parsing this file without a shared build step.

function decodeComponent(s) {
  try {
    return decodeURIComponent(String(s).replace(/\+/g, ' '));
  } catch {
    return s;
  }
}

/** Parse a query-string-like blob ("a=1&b=2") into ordered [key,value] pairs. */
function parsePairs(qs) {
  const pairs = [];
  if (!qs) return pairs;
  const raw = qs.startsWith('?') ? qs.slice(1) : qs;
  if (!raw) return pairs;
  for (const part of raw.split('&')) {
    if (part === '') continue;
    const eq = part.indexOf('=');
    let k, v;
    if (eq === -1) { k = part; v = ''; }
    else { k = part.slice(0, eq); v = part.slice(eq + 1); }
    pairs.push([decodeComponent(k), decodeComponent(v)]);
  }
  return pairs;
}

function safeParseUrl(u) {
  try {
    return new URL(String(u));
  } catch {
    return null;
  }
}

/* ── param dictionary ──────────────────────────────────────────────── */
// category: 'slot' | 'consent' | 'correlator/session' | 'sdk/environment' | 'video' | 'targeting' | 'other'

export const GAM_PARAM_DICTIONARY = {
  iu: { name: 'Ad unit path', meaning: 'The GAM ad unit path being requested (network code + ad unit hierarchy).', category: 'slot' },
  sz: { name: 'Sizes', meaning: 'Requested creative sizes for this slot, e.g. 320x50.', category: 'slot' },
  ciu_szs: { name: 'Companion sizes', meaning: 'Sizes offered for companion ad slots (used in video/companion setups).', category: 'slot' },
  scor: { name: 'Slot correlator', meaning: 'Per-slot correlator used to tie a specific slot to its impression/click within a page-level session.', category: 'slot' },

  cust_params: { name: 'Custom targeting params', meaning: 'Form-encoded blob of key-value targeting pairs (Prebid/APS hooks, page-level key-values, etc) — decoded separately below.', category: 'targeting' },
  ppid: { name: 'Publisher-provided ID', meaning: 'Publisher-supplied identifier used for frequency capping / audience targeting instead of device/cookie IDs.', category: 'targeting' },
  eid: { name: 'Extended ID', meaning: 'Encoded third-party/first-party extended identifier(s) passed for identity-based targeting.', category: 'targeting' },
  an: { name: 'App name', meaning: 'Application name/bundle identifier sending the request.', category: 'targeting' },
  url: { name: 'Referring URL', meaning: 'The content URL or app-declared URL associated with this ad request.', category: 'targeting' },

  correlator: { name: 'Correlator', meaning: 'Session-scoped random value shared by every ad request on the same page/app load — used to match a request to its own impression/click and to group slots refreshed together.', category: 'correlator/session' },
  gdfp_req: { name: 'GDFP request flag', meaning: 'Legacy flag marking this as a GPT/GDFP-formatted ad request (always 1).', category: 'correlator/session' },
  msid: { name: 'Master slot ID', meaning: 'Identifies the GPT library/page session issuing the request.', category: 'correlator/session' },

  gdpr: { name: 'GDPR applies', meaning: 'Whether GDPR applies to this user (1 = yes, 0 = no).', category: 'consent' },
  gdpr_consent: { name: 'GDPR consent string', meaning: 'IAB TCF v2 consent string describing the user\'s consent choices.', category: 'consent' },
  us_privacy: { name: 'US Privacy string', meaning: 'IAB CCPA/US Privacy string (e.g. opt-out-of-sale status).', category: 'consent' },
  gpp: { name: 'GPP string', meaning: 'IAB Global Privacy Platform consent string covering multiple US state/regional privacy laws.', category: 'consent' },
  gpp_sid: { name: 'GPP section IDs', meaning: 'Which GPP sections are present/applicable in the gpp string.', category: 'consent' },
  npa: { name: 'Non-personalized ads', meaning: 'When 1, requests non-personalized ads only (no interest-based targeting).', category: 'consent' },
  rdp: { name: 'Restricted data processing', meaning: 'COPPA-style flag requesting restricted data processing for this request.', category: 'consent' },
  ltd: { name: 'Limited ads', meaning: 'Requests limited ads serving (reduced data use), typically paired with GPP US state signals.', category: 'consent' },
  tfcd: { name: 'Tag for child-directed treatment', meaning: 'COPPA flag marking this request as subject to child-directed treatment.', category: 'consent' },
  tfua: { name: 'Tag for under-age-of-consent', meaning: 'Marks this request as subject to under-age-of-consent (GDPR-age) treatment.', category: 'consent' },
  rdid: { name: 'Raw device ID', meaning: 'Raw advertising identifier (IDFA/AAID) for the device, used for ad targeting/measurement.', category: 'consent' },
  idtype: { name: 'ID type', meaning: 'Identifies which identifier scheme rdid is using (e.g. idfa, aaid).', category: 'consent' },
  is_lat: { name: 'Limit ad tracking', meaning: 'Whether the user has limited ad tracking enabled (1 = limited).', category: 'consent' },
  att: { name: 'App Tracking Transparency status', meaning: 'iOS ATT authorization status reported by the app (e.g. authorized, denied, notDetermined).', category: 'consent' },

  env: { name: 'Environment', meaning: 'Declares the calling environment, e.g. "vp" for video player / instream.', category: 'sdk/environment' },
  output: { name: 'Output format', meaning: 'Requested response format, e.g. "xml_vast3" for video ad responses.', category: 'sdk/environment' },
  impl: { name: 'Implementation', meaning: 'Ad implementation type used by the SDK (e.g. "s" for synchronous JS, "fifs" for friendly iframe).', category: 'sdk/environment' },
  unviewed_position_start: { name: 'Unviewed position start', meaning: 'Signals the SDK supports delayed/viewability-gated rendering (lazy load until in-view).', category: 'sdk/environment' },
  sdkv: { name: 'SDK version', meaning: 'Version string of the ads SDK issuing the request.', category: 'sdk/environment' },
  sdk_apis: { name: 'Supported SDK APIs', meaning: 'List of supported ad APIs (e.g. VPAID/OMID) advertised by the SDK.', category: 'sdk/environment' },
  hl: { name: 'Language', meaning: 'Locale/language hint for the ad request.', category: 'sdk/environment' },
  js: { name: 'JS support flag', meaning: 'Indicates JavaScript support level of the requesting environment.', category: 'sdk/environment' },
  jsv: { name: 'GPT JS version', meaning: 'Version of the GPT/JS library making the request.', category: 'sdk/environment' },
  lver: { name: 'Library version', meaning: 'Version of the ad SDK/library (IMA, GMA, etc) making the request.', category: 'sdk/environment' },
  dt: { name: 'Device type', meaning: 'Numeric device-type code reported to GAM (phone/tablet/CTV/etc).', category: 'sdk/environment' },

  vid: { name: 'Video content ID', meaning: 'Identifier of the video content this ad break is attached to, for content-aware targeting/reporting.', category: 'video' },
  cmsid: { name: 'CMS ID', meaning: 'Content management system ID for the video content, paired with vid.', category: 'video' },
  vpos: { name: 'Video position', meaning: 'Where in the video stream this ad break occurs (preroll/midroll/postroll).', category: 'video' },
  vpmute: { name: 'Video player muted', meaning: 'Whether the video player is muted at request time (1 = muted).', category: 'video' },
  vpa: { name: 'Video playback auto-start', meaning: 'Whether video playback auto-starts (1) or is click-to-play (0).', category: 'video' },
  wta: { name: 'Want translucent ad', meaning: 'Requests a translucent/overlay-compatible ad response for the video player.', category: 'video' },
  plcmt: { name: 'Placement type', meaning: 'OpenRTB 2.6 video placement type (instream/accompanying/interstitial/etc).', category: 'video' },
};

const GAM_AD_HOST_RE = /(^|\.)doubleclick\.net$|(^|\.)securepubads\.g\.doubleclick\.net$/i;
const GAM_AD_PATH_RE = /\/gampad\/ads/i;

function isGamAdEndpoint(hostname, pathname) {
  if (!hostname) return false;
  const hostMatch = GAM_AD_HOST_RE.test(hostname);
  const pathMatch = GAM_AD_PATH_RE.test(pathname || '');
  // Any doubleclick.net host serving /gampad/ads, or securepubads specifically.
  return (hostMatch && (pathMatch || /securepubads/i.test(hostname))) || pathMatch;
}

/** Known cust_params key meanings — unknown keys pass through with no meaning. */
const CUST_PARAM_MEANINGS = {
  hb_pb: 'Prebid winning bucketed price',
  hb_bidder: 'Prebid winning bidder name',
  hb_cache_id: 'Prebid Server cache ID for the winning creative (used to fetch cached markup)',
  hb_adid: 'Prebid winning ad ID',
  hb_size: 'Prebid winning creative size',
  hb_source: 'Prebid bid source (server/client)',
  hb_format: 'Prebid winning bid format (banner/video/native)',
  amzn_b: 'Amazon APS bid tokens',
  amznslots: 'Amazon APS bid tokens',
  pageType: 'Publisher-declared page/content type used for targeting',
};

function explainCustParams(rawValue) {
  const pairs = parsePairs(rawValue);
  return pairs.map(([key, value]) => {
    const meaning = CUST_PARAM_MEANINGS[key];
    return meaning ? { key, value, meaning } : { key, value };
  });
}

function truthy1(v) {
  return v === '1' || v === 1 || v === 'true';
}

function buildMissingFlags(paramMap) {
  const missing = [];

  const hasAnyConsentParam = ['gdpr', 'gdpr_consent', 'us_privacy', 'gpp'].some((k) => paramMap.has(k));
  if (!hasAnyConsentParam) {
    missing.push({ key: 'consent', why: 'no privacy signal on this request (no gdpr/gdpr_consent/us_privacy/gpp params)' });
  }

  if (!paramMap.has('correlator')) {
    missing.push({ key: 'correlator', why: 'no correlator: impression matching impossible' });
  }

  const hasRdid = paramMap.has('rdid');
  const hasIdtype = paramMap.has('idtype');
  if (hasRdid && !hasIdtype) {
    missing.push({ key: 'idtype', why: 'rdid present without idtype — cannot tell which identifier scheme (IDFA/AAID) is being sent' });
  }

  const isLat = paramMap.get('is_lat');
  const att = paramMap.get('att');
  const limitedTracking = truthy1(isLat) || (typeof att === 'string' && /denied/i.test(att));
  if (hasRdid && limitedTracking) {
    missing.push({ key: 'rdid', why: 'device ID sent despite limited tracking (is_lat=1 or ATT denied) — potential privacy/policy violation' });
  }

  return missing;
}

export function explainGamUrl(url) {
  const parsed = safeParseUrl(url);
  if (!parsed) {
    return {
      ok: false,
      reason: 'could not parse URL',
      host: undefined,
      isGamAdRequest: false,
      params: [],
      custParams: [],
      missing: [],
    };
  }

  try {
    const host = parsed.hostname;
    const isGamAdRequest = isGamAdEndpoint(host, parsed.pathname);

    const pairs = parsePairs(parsed.search);
    const paramMap = new Map(pairs);

    const params = pairs.map(([key, value]) => {
      const dict = GAM_PARAM_DICTIONARY[key];
      const decodedValue = value !== decodeComponent(value) ? decodeComponent(value) : undefined;
      const base = {
        key,
        value,
        name: dict ? dict.name : key,
        meaning: dict ? dict.meaning : 'Unrecognized parameter',
        category: dict ? dict.category : 'other',
      };
      if (decodedValue !== undefined && decodedValue !== value) base.decodedValue = decodedValue;
      return base;
    });

    const custParamsRaw = paramMap.get('cust_params');
    const custParams = custParamsRaw ? explainCustParams(custParamsRaw) : [];
    const custParamsMap = new Map(custParams.map((c) => [c.key, c.value]));

    const missing = buildMissingFlags(paramMap);

    return {
      ok: true,
      host,
      isGamAdRequest,
      params,
      custParams,
      missing,
    };
  } catch (e) {
    return {
      ok: false,
      reason: 'unexpected error explaining URL: ' + (e && e.message ? e.message : String(e)),
      host: parsed ? parsed.hostname : undefined,
      isGamAdRequest: false,
      params: [],
      custParams: [],
      missing: [],
    };
  }
}

export default explainGamUrl;
